import type { Identifier } from "@medplum/fhirtypes";
import { PersistenceMappingError } from "./errors";
import type { EntityIdentity } from "./types";

interface IdentifierBearingResource {
  resourceType: string;
  id?: string;
  identifier?: Identifier[];
}

/**
 * FHIR representation of one stable domain entity identity.
 *
 * The codec owns Identifier mechanics. The aggregate owns identity, the mapper
 * declares its representation, and the session privately resolves the FHIR
 * resource locator from the declaration returned by `forEntity`.
 */
export interface FhirIdentifierIdentity<Id extends string> {
  readonly system: string;
  read(resource: IdentifierBearingResource): Id;
  write(existing: Identifier[] | undefined, id: Id): Identifier[];
  forEntity<E>(value: (entity: E) => Id): EntityIdentity<E>;
  scopedTo(parentId: string): Pick<FhirIdentifierIdentity<Id>, "read" | "write">;
}

export function fhirIdentifierIdentity<Id extends string>(system: string): FhirIdentifierIdentity<Id> {
  const identity: FhirIdentifierIdentity<Id> = {
    system,

    scopedTo(parentId) {
      const prefix = `${parentId}|`;
      return {
        read(resource) {
          const value = identity.read(resource);
          if (!value.startsWith(prefix) || value.length === prefix.length) {
            throw new PersistenceMappingError(
              `FHIR ${resource.resourceType} row '${resource.id ?? "<unknown>"}' must have a populated member identity scoped to '${parentId}'`,
            );
          }
          return value.slice(prefix.length) as Id;
        },
        write(existing, id) {
          return identity.write(existing, `${prefix}${id}` as Id);
        },
      };
    },

    read(resource) {
      const matches = (resource.identifier ?? []).filter((identifier) => identifier.system === system);
      if (matches.length !== 1 || !matches[0]?.value) {
        throw new PersistenceMappingError(
          `FHIR ${resource.resourceType}/${resource.id ?? "<unknown>"} has ${matches.length} domain identifiers for '${system}'; expected exactly one populated identifier`,
        );
      }
      return matches[0].value as Id;
    },

    write(existing, id) {
      const identifiers = existing ?? [];
      const matches = identifiers.filter((identifier) => identifier.system === system);
      if (matches.length > 1) {
        throw new PersistenceMappingError(
          `Cannot write domain identity '${system}|${id}': found ${matches.length} identifiers for '${system}'`,
        );
      }
      if (matches.length === 0) return [...identifiers, { system, value: id }];
      if (matches[0]?.value === id) return identifiers;
      return identifiers.map((identifier) => (identifier.system === system ? { system, value: id } : identifier));
    },

    forEntity<E>(value: (entity: E) => Id): EntityIdentity<E> {
      return { system, value };
    },
  };
  return identity;
}

export function identitySystems(identity: EntityIdentity<never>): readonly string[] {
  return identity.systems ?? [identity.system];
}

export function identitySystem<E>(identity: EntityIdentity<E>, entity: E): string {
  return identity.systemFor?.(entity) ?? identity.system;
}
